// -----------------------------------------------------------------------------
// AddNotePanel  (Wave A)
//
// Action panel body for ADD_NOTE.
// Fields: note textarea, optional "Mark as call note" toggle.
// Submit: POST note to leads API, then call onComplete.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    Stack,
} from '@mui/material';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';
import Iconify from 'src/components/Iconify';
import { CONTACT_MADE_NOTE } from 'src/constants/appointmentReason';

export default function AddNotePanel({
    item,
    practiceId,
    leadId,
    leadDetail,
    onComplete,
    onClose,
    onBack,
    backLabel,
}) {
    const [note, setNote] = useState('');
    const [isCallNote, setIsCallNote] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!note.trim()) {
            dispatch(updateError({ success: false, message: 'Please enter a note' }));
            return;
        }
        setSubmitting(true);
        try {
            const treatmentId = leadDetail?.lead_treatments?.[0]?.id || null;
            const res = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/notes/`,
                'post',
                {
                    lead: leadId,
                    // LeadNote API stores free text in `additional_notes`; `description` is
                    // ignored by the serializer (was creating empty note='OTHER' orphans).
                    additional_notes: note.trim(),
                    // A plain note is not contact. Ticking "call" makes it one, and
                    // that needs a note type as well as a channel to register at
                    // all — see CONTACT_MADE_NOTE.
                    ...(isCallNote ? { contacted_via: 'PHONE', note: CONTACT_MADE_NOTE } : {}),
                    ...(treatmentId ? { treatment: treatmentId } : {}),
                }
            );
            if (res.status === 201) {
                dispatch(updateError({ success: true, message: 'Note added' }));
                onComplete?.(item);
            } else {
                const msg = res?.data?.detail || res?.data?.additional_notes?.[0] || 'Failed to add note';
                dispatch(updateError({ success: false, message: msg }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Something went wrong' }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            data-testid="add-note-panel"
            sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.75,
                height: '100%',
                overflowY: 'auto',
            }}
        >
            {/* Icon header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: 'rgba(0, 113, 227, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Iconify icon="mdi:note-edit-outline" width={18} height={18} sx={{ color: '#0071e3' }} />
                </Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.012em' }}>
                    Add note
                </Typography>
            </Box>

            {/* Note textarea */}
            <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                    Note
                </Typography>
                <TextField
                    data-testid="add-note-textarea"
                    multiline
                    rows={2}
                    fullWidth
                    size="small"
                    placeholder="Add your note here…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    autoFocus
                    sx={{
                        '& .MuiInputBase-root': { alignItems: 'flex-start' },
                        '& textarea': { resize: 'vertical', minHeight: '40px' },
                    }}
                />
            </Box>

            {/* Call note toggle */}
            <FormControlLabel
                data-testid="add-note-call-toggle"
                control={
                    <Checkbox
                        size="small"
                        checked={isCallNote}
                        onChange={(e) => setIsCallNote(e.target.checked)}
                        sx={{ color: '#6e6e73', '&.Mui-checked': { color: '#0071e3' } }}
                    />
                }
                label={<Typography sx={{ fontSize: '13px', color: '#1d1d1f' }}>Mark as call note</Typography>}
                sx={{ m: 0 }}
            />

            {/* Actions — back sits to the LEFT of the primary button (matches LogCallPanel). */}
            <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                {onBack && (
                    <Button
                        data-testid="add-note-back"
                        variant="outlined"
                        size="small"
                        onClick={() => onBack()}
                        disabled={submitting}
                        startIcon={<Iconify icon="eva:arrow-ios-back-fill" width={14} height={14} />}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: '#078DEE',
                            borderColor: 'rgba(7,141,238,0.4)',
                            '&:hover': { borderColor: '#078DEE', bgcolor: 'rgba(7,141,238,0.06)' },
                        }}
                    >
                        {backLabel || 'Back'}
                    </Button>
                )}
                <Button
                    data-testid="add-note-submit"
                    variant="contained"
                    disableElevation
                    onClick={handleSubmit}
                    disabled={submitting || !note.trim()}
                    startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Iconify icon="mdi:check" width={14} height={14} />}
                    sx={{
                        flex: 1,
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        bgcolor: '#0071e3',
                        '&:hover': { bgcolor: '#005cc5' },
                        boxShadow: 'none',
                    }}
                >
                    {submitting ? 'Saving…' : 'Save note'}
                </Button>
                {!onBack && (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onClose?.()}
                        disabled={submitting}
                        sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '13px' }}
                    >
                        Cancel
                    </Button>
                )}
            </Stack>
        </Box>
    );
}

AddNotePanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    /** When provided, renders a back button to the LEFT of "Save note" (replaces Cancel). */
    onBack: PropTypes.func,
    /** Label for the back button (e.g. "Back"). */
    backLabel: PropTypes.string,
};

AddNotePanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    leadDetail: null,
    onComplete: null,
    onBack: null,
    backLabel: 'Back',
};
